import { Button, Form, Input, InputNumber, Select, Space, TreeSelect } from 'antd';
import { useEffect, useMemo, useState } from 'react';
import { listRoutesByUserType } from 'assets/routes/listRoutesByUserType';

const USER_TYPES = [
  { value: 'administrador', label: 'Administrador' },
  { value: 'especialista', label: 'Especialista' },
  { value: 'ejecutivo', label: 'Ejecutivo' },
  { value: 'liftlogic', label: 'Liftlogic' },
];

const CUSTOM_ROUTE_SENTINEL = '__custom__';

const itemsToTreeData = (items, excludeId) => {
  if (!items) return [];
  const byId = new Map();
  items.forEach((it) => byId.set(it.id, { ...it, children: [] }));
  const roots = [];
  byId.forEach((n) => {
    if (n.id === excludeId) return;
    if (n.parentId != null && byId.has(n.parentId) && n.parentId !== excludeId) {
      byId.get(n.parentId).children.push(n);
    } else {
      roots.push(n);
    }
  });
  const map = (n) => ({
    value: n.id,
    title: n.label,
    children: n.children.map(map),
  });
  return roots.map(map);
};

// Campo controlado para la ruta de un menu_item. Si el userType elegido tiene
// rutas registradas, muestra un Select con esas rutas + opción "Ruta
// personalizada" al final. Al elegirla, se cambia a Input libre. Si el
// userType no tiene rutas registradas (ej. administrador, que solo tiene
// root + catchAll), cae directo a Input.
//
// Props:
//   - value / onChange: API estándar de Form.Item (antd)
//   - userType: string actual del form (se mira con useWatch arriba)
function RouteField({ value, onChange, userType }) {
  const routes = useMemo(() => listRoutesByUserType(userType), [userType]);
  const hasRegisteredRoutes = routes.length > 0;

  // Un valor es "custom" si no está en la lista de rutas del userType.
  // Arrancamos en modo custom si el value inicial no coincide con ninguna.
  const valueIsRegistered = value && routes.includes(value);
  const [mode, setMode] = useState(
    !hasRegisteredRoutes || (value && !valueIsRegistered) ? 'custom' : 'select'
  );

  useEffect(() => {
    if (!hasRegisteredRoutes) {
      setMode('custom');
      return;
    }
    // Si cambia el userType y el value actual ya no aplica a esa lista,
    // cambio a custom para no "borrar" silenciosamente lo que el user escribió.
    if (value && !routes.includes(value)) {
      setMode('custom');
    }
  }, [userType, hasRegisteredRoutes, routes, value]);

  if (mode === 'custom') {
    return (
      <Space.Compact style={{ width: '100%' }}>
        <Input
          value={value ?? ''}
          onChange={(e) => onChange?.(e.target.value)}
          placeholder="/admin/mi-ruta"
        />
        {hasRegisteredRoutes ? (
          <Button
            onClick={() => {
              onChange?.(undefined);
              setMode('select');
            }}
          >
            Usar lista
          </Button>
        ) : null}
      </Space.Compact>
    );
  }

  const options = [
    ...routes.map((r) => ({ value: r, label: r })),
    { value: CUSTOM_ROUTE_SENTINEL, label: '— Ruta personalizada… —' },
  ];

  return (
    <Select
      value={value}
      placeholder="Elegí una ruta del userType"
      options={options}
      showSearch
      optionFilterProp="label"
      onChange={(next) => {
        if (next === CUSTOM_ROUTE_SENTINEL) {
          setMode('custom');
          onChange?.('');
          return;
        }
        onChange?.(next);
      }}
    />
  );
}

const MenuItemForm = ({ initialValues, items, onSubmit, onCancel }) => {
  const [form] = Form.useForm();
  const watchedUserType = Form.useWatch('userType', form);

  useEffect(() => {
    form.resetFields();
    if (initialValues) form.setFieldsValue(initialValues);
  }, [initialValues, form]);

  const handleFinish = async (values) => {
    await onSubmit(values);
  };

  return (
    <Form
      form={form}
      layout="vertical"
      initialValues={{ order: 0, ...initialValues }}
      onFinish={handleFinish}
    >
      <Form.Item name="label" label="Label" rules={[{ required: true, message: 'Requerido' }]}>
        <Input />
      </Form.Item>
      <Form.Item name="key" label="Key" rules={[{ required: true, message: 'Requerido' }]}>
        <Input />
      </Form.Item>
      <Form.Item name="userType" label="User type">
        <Select allowClear options={USER_TYPES} />
      </Form.Item>
      <Form.Item name="route" label="Route" rules={[{ required: true, message: 'Requerido' }]}>
        <RouteField userType={watchedUserType} />
      </Form.Item>
      <Form.Item name="icon" label="Icon (nombre de @ant-design/icons)">
        <Input placeholder="HomeOutlined" />
      </Form.Item>
      <Form.Item name="parentId" label="Parent">
        <TreeSelect
          allowClear
          treeDefaultExpandAll
          placeholder="— ninguno (raíz) —"
          treeData={itemsToTreeData(items, initialValues?.id)}
        />
      </Form.Item>
      <Form.Item name="order" label="Order">
        <InputNumber min={0} />
      </Form.Item>
      <Form.Item>
        <Space>
          <Button type="primary" htmlType="submit">
            Guardar
          </Button>
          <Button onClick={onCancel}>Cancelar</Button>
        </Space>
      </Form.Item>
    </Form>
  );
};

export default MenuItemForm;
